import type { ParsedPdf } from "../types";
import {
  MAX_EXTRACTED_TEXT,
  MAX_PDF_PAGES,
  MAX_BOOK_SIZE,
} from "../constants";
import { loadPdfEngine } from "./pdfEngine";

export function validatePdf(file: File): void {
  const looksLikePdf =
    file.type === "application/pdf" || file.name.toLocaleLowerCase().endsWith(".pdf");

  if (!looksLikePdf) {
    throw new Error("Lütfen PDF formatında bir belge seç.");
  }
  if (file.size > MAX_BOOK_SIZE) {
    throw new Error("PDF en fazla 250 MB olabilir.");
  }
  if (file.size === 0) {
    throw new Error("Bu PDF boş görünüyor.");
  }
}

export async function parsePdf(
  file: File,
  onProgress?: (current: number, total: number) => void,
): Promise<ParsedPdf> {
  validatePdf(file);

  const { getDocument } = await loadPdfEngine();
  const data = new Uint8Array(await file.arrayBuffer());
  const hasPdfSignature =
    data.length >= 5 &&
    data[0] === 0x25 &&
    data[1] === 0x50 &&
    data[2] === 0x44 &&
    data[3] === 0x46 &&
    data[4] === 0x2d;
  if (!hasPdfSignature) {
    throw new Error("Dosyanın içeriği geçerli bir PDF değil.");
  }

  const loadingTask = getDocument({ data });
  const pdf = await loadingTask.promise;
  const pageCount = pdf.numPages;
  const pages = [];
  let extractedCharacterCount = 0;

  try {
    if (pageCount > MAX_PDF_PAGES) {
      throw new Error(`PDF en fazla ${MAX_PDF_PAGES} sayfa olabilir.`);
    }

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      const text = content.items
        .filter((item): item is (typeof content.items)[number] & { str: string } =>
          "str" in item,
        )
        .map((item) => item.str)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();

      extractedCharacterCount += text.length;
      if (extractedCharacterCount > MAX_EXTRACTED_TEXT) {
        page.cleanup();
        throw new Error("PDF çok fazla metin içeriyor; daha küçük bir belge seç.");
      }

      pages.push({ pageNumber, text });
      onProgress?.(pageNumber, pdf.numPages);
      page.cleanup();
    }
  } finally {
    await loadingTask.destroy();
  }

  if (!pages.some((page) => page.text.length > 0)) {
    throw new Error(
      "Bu PDF taranmış görünüyor; seçilebilir metin bulunamadı. OCR desteği sonraki sürümde eklenebilir.",
    );
  }

  return {
    name: file.name,
    size: file.size,
    pageCount,
    pages,
    format: "pdf",
  };
}
